import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, addDays, isToday, isBefore, startOfDay } from 'date-fns';
import { Clock, MapPin, CheckCircle, ShoppingCart, XCircle, Plus, Minus, Phone, ExternalLink, CreditCard, Ticket, AlertTriangle, RefreshCw, Shield, Loader2, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { getKioskToken, fetchKioskTransactionsData, KIOSK_API_BASE } from '../lib/kioskTokenManager';

// Normalize backend URL (supports both base host and full API base)
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

// WebSocket URL helper
const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

// Kiosk API endpoints are imported from shared kioskTokenManager

// Helper function to format 24-hour time to 12-hour AM/PM
const formatTimeToAMPM = (time24) => {
  const [hours] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}${period}`;
};

// Helper function to format currency with trailing zeroes
const formatCurrency = (amount) => {
  return `₱${Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to parse time strings
const parseTimeToHour = (timeStr) => {
  if (!timeStr) return 0;
  if (timeStr.includes(':')) {
    return parseInt(timeStr.split(':')[0]);
  }
  const match = timeStr.match(/(\d+)(AM|PM)/i);
  if (match) {
    let hour = parseInt(match[1]);
    if (match[2].toUpperCase() === 'PM' && hour !== 12) hour += 12;
    if (match[2].toUpperCase() === 'AM' && hour === 12) hour = 0;
    return hour;
  }
  return parseInt(timeStr) || 0;
};

// Normalize time to 24-hour format
const normalizeTime = (timeStr) => {
  const hour = parseTimeToHour(timeStr);
  return `${String(hour).padStart(2, '0')}:00`;
};

const BookingPage = () => {
  const { venueId } = useParams();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();
  
  const [venue, setVenue] = useState(null);
  const [venueInfo, setVenueInfo] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Kiosk state
  const [kioskTransactions, setKioskTransactions] = useState([]);
  const [loadingKiosk, setLoadingKiosk] = useState(false);
  const [selectedKioskTransaction, setSelectedKioskTransaction] = useState(null);
  const [showKioskModal, setShowKioskModal] = useState(false);
  const [kioskModalLoading, setKioskModalLoading] = useState(false);
  
  // Booking state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedCourt, setSelectedCourt] = useState(searchParams.get('court') || '');
  const [numPlayers, setNumPlayers] = useState(2);
  const [selectedSlots, setSelectedSlots] = useState([]); // Changed to array for multiple selection
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('gcash'); // Default to gcash e-wallet
  const [ewalletPhone, setEwalletPhone] = useState(''); // Phone number for e-wallet payments
  const [step, setStep] = useState(1);
  
  // Payment confirmation state
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  // const [enableMockPayment, setEnableMockPayment] = useState(false);
  const [useMockPayment, setUseMockPayment] = useState(false);
  
  // Reserve only state (pay at counter)
  const [showReserveConfirmation, setShowReserveConfirmation] = useState(false);
  const [processingReserve, setProcessingReserve] = useState(false);
  
  // Test amount override for production gateway testing (temporary)
  // const [useTestAmount, setUseTestAmount] = useState(false);
  // const [testAmount, setTestAmount] = useState(1);

  // Availability fetch state - prevents interaction with stale data
  const [fetchingAvailability, setFetchingAvailability] = useState(false);

  // Track which date+court combo the current availability belongs to
  const [availabilityVersion, setAvailabilityVersion] = useState('');

  // Generation counter to discard stale async fetch responses
  const fetchGenRef = useRef(0);

  // Payment method options - selectable for e-wallet direct payments
  const paymentMethodOptions = [
    { id: 'gcash', src: '/assets/payment_methods/gcash.png', alt: 'GCash', label: 'GCash', isEwallet: true },
    { id: 'maya', src: '/assets/payment_methods/maya.png', alt: 'Maya', label: 'Maya', isEwallet: true },
    { id: 'grabpay', src: '/assets/payment_methods/grabpay.png', alt: 'GrabPay', label: 'GrabPay', isEwallet: true },
    { id: 'palawanpay', src: '/assets/payment_methods/palawan_pay.png', alt: 'PalawanPay', label: 'PalawanPay', isEwallet: true },
    { id: 'card', src: '/assets/payment_methods/master_visa_1.png', alt: 'Card', label: 'Debit/Credit Card', isEwallet: false },
  ];

  // Legacy payment method images for display only (not selectable)
  const paymentMethodImages = [
    { src: '/assets/payment_methods/UnionBank.png', alt: 'UnionBank' },
    { src: '/assets/payment_methods/master_visa_1.png', alt: 'Visa/Mastercard' },
    { src: '/assets/payment_methods/gcash.png', alt: 'GCash' },
    { src: '/assets/payment_methods/maya.png', alt: 'Maya' },
    { src: '/assets/payment_methods/grabpay.png', alt: 'GrabPay' },
    { src: '/assets/payment_methods/Instapay.png', alt: 'Instapay' },
  ];
  
  // Check if selected payment method is an e-wallet
  const isEwalletSelected = ['gcash', 'maya', 'grabpay', 'palawanpay'].includes(selectedPaymentMethod);
  
  // Validate phone number for e-wallet payments (Philippine format)
  const isValidPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    // Accept 09XXXXXXXXX (11 digits) or 639XXXXXXXXX (12 digits)
    return /^(09\d{9}|639\d{9})$/.test(cleaned);
  };

  // Calculate duration from selected slots
  const duration = selectedSlots.length;

  // Fetch kiosk transactions to check for reservations (uses shared token manager)
  const fetchKioskTransactions = useCallback(async () => {
    setLoadingKiosk(true);
    try {
      const txArray = await fetchKioskTransactionsData();
      setKioskTransactions(txArray);
    } catch (error) {
      console.error('Failed to fetch kiosk transactions:', error);
    } finally {
      setLoadingKiosk(false);
    }
  }, []);

  // Handle kiosk transaction click - fetch full details
  const handleKioskTransactionClick = async (transactionCode) => {
    setShowKioskModal(true);
    setKioskModalLoading(true);
    setSelectedKioskTransaction(null);

    try {
      const kioskToken = await getKioskToken();
      if (!kioskToken) {
        toast.error('Failed to authenticate with kiosk API');
        setShowKioskModal(false);
        return;
      }

      const url = `${KIOSK_API_BASE}/transactions/${transactionCode}`;
      const res = await fetch(url, {
        headers: { 'X-KIOSK-TOKEN': kioskToken },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch transaction details: ${res.status}`);
      }

      const responseData = await res.json();
      const txData = responseData?.data || responseData;
      setSelectedKioskTransaction(txData);
    } catch (error) {
      console.error('Failed to fetch transaction details:', error);
      toast.error(`Failed to load transaction: ${error.message}`);
      setShowKioskModal(false);
    } finally {
      setKioskModalLoading(false);
    }
  };

  // Close kiosk modal
  const closeKioskModal = () => {
    setShowKioskModal(false);
    setSelectedKioskTransaction(null);
  };

  // Build index of kiosk reservations for the selected date to mark slots
  const kioskReservedSlots = useMemo(() => {
    const reserved = new Map(); // key: courtId|slotTime, value: transaction
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    
    for (const tx of kioskTransactions) {
      // Skip paid/cancelled transactions - only show pending reservations
      if (tx.status === 'paid' || tx.status === 'cancelled') continue;
      
      const bookingInfos = tx.booking_infos || [];
      for (const info of bookingInfos) {
        const bookingDate = info.booking_date;
        if (bookingDate !== selectedDateStr) continue;
        
        const courtNo = info.court_no || info.court || 1;
        const courtId = `court_${courtNo}`;
        
        // Get time slots
        let slots = [];
        if (Array.isArray(info.time_slots) && info.time_slots.length > 0) {
          slots = info.time_slots.map(normalizeTime);
        } else if (info.booking_time_start && info.booking_time_end) {
          const startHour = parseTimeToHour(info.booking_time_start);
          const endHour = parseTimeToHour(info.booking_time_end);
          for (let h = startHour; h < endHour; h++) {
            slots.push(`${String(h).padStart(2, '0')}:00`);
          }
        }
        
        for (const slot of slots) {
          const key = `${courtId}|${slot}`;
          reserved.set(key, tx);
        }
      }
    }
    
    return reserved;
  }, [kioskTransactions, selectedDate]);

  // Check if slot is reserved by kiosk
  const isSlotKioskReserved = (courtId, slotTime) => {
    const key = `${courtId}|${slotTime}`;
    return kioskReservedSlots.has(key);
  };

  // Get kiosk transaction for a slot
  const getKioskTransactionForSlot = (courtId, slotTime) => {
    const key = `${courtId}|${slotTime}`;
    return kioskReservedSlots.get(key);
  };

  // Check if a time slot has passed (for today only)
  const isSlotPassed = (slotTime) => {
    if (!isToday(selectedDate)) return false;
    
    const now = new Date();
    const [hours] = slotTime.split(':').map(Number);
    const slotDate = new Date(selectedDate);
    slotDate.setHours(hours, 0, 0, 0);
    
    // Only past hours are unavailable - current hour is bookable
    const currentHour = new Date(now);
    currentHour.setMinutes(0, 0, 0);
    return slotDate < currentHour;
  };

  // When date or court changes: clear stale data immediately, mark loading
  useEffect(() => {
    fetchGenRef.current++;          // Invalidate any in-flight fetches
    setSelectedSlots([]);
    setAvailability([]);             // Flush stale slots so nothing renders during fetch
    setFetchingAvailability(true);   // Block interaction immediately, before fetch effect runs
    setAvailabilityVersion('');      // Invalidate version so courtSlots is empty
    // Also flush kiosk overlay for selected slots (kiosk data may be stale)
  }, [format(selectedDate, 'yyyy-MM-dd'), selectedCourt]);

  const fetchAvailability = useCallback(async () => {
    if (!venue || !selectedDate) return;
    
    // Capture generation so we can discard this response if a newer fetch starts
    const thisGen = ++fetchGenRef.current;
    setFetchingAvailability(true);
    
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      console.log('Fetching availability for date:', dateStr, 'court:', selectedCourt, 'venueId:', venueId);
      
      const response = await fetch(`${API}/venues/${venueId}/availability?date=${dateStr}`);
      
      // Discard stale response — a newer date/court fetch superseded this one
      if (fetchGenRef.current !== thisGen) {
        console.log('Stale availability response discarded (gen', thisGen, 'vs current', fetchGenRef.current, ')');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        let newSlots = data.slots || [];
        console.log('Availability data received:', data);
        
        // Store the version so the rendering layer knows this data is fresh
        const versionKey = `${dateStr}|${selectedCourt}`;
        setAvailabilityVersion(versionKey);
        setAvailability(newSlots);
        
        // Auto-deselect any selected slots that are no longer available
        setSelectedSlots(prev => {
          const unavailable = prev.filter(slotTime => {
            const slotInfo = newSlots.find(s => s.court_id === selectedCourt && s.start_time === slotTime);
            return slotInfo && !slotInfo.available;
          });
          if (unavailable.length > 0) {
            toast.warning(`${unavailable.length} slot(s) became unavailable and were deselected.`);
            return prev.filter(s => !unavailable.includes(s));
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      // Only clear loading state if this is still the current fetch
      if (fetchGenRef.current === thisGen) {
        setFetchingAvailability(false);
      }
    }
  }, [venue, selectedDate, venueId, selectedCourt]);

  // Fetch availability when date or court changes
  useEffect(() => {
    fetchAvailability();
  }, [format(selectedDate, 'yyyy-MM-dd'), selectedCourt, fetchAvailability]);

  // Re-fetch availability when step changes (e.g., going back to step 1 after time on step 2/3)
  const stepRef = useRef(step);
  useEffect(() => {
    if (step === 1 && stepRef.current !== 1) {
      // User came back to step 1 — refresh availability to catch any changes
      setFetchingAvailability(true);
      fetchAvailability();
      fetchKioskTransactions();
    }
    stepRef.current = step;
  }, [step, fetchAvailability, fetchKioskTransactions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venueRes, addonsRes, venueInfoRes, paymentSettingsRes] = await Promise.all([
          fetch(`${API}/venues/${venueId}`),
          fetch(`${API}/addons`),
          fetch(`${API}/venue-info`),
          fetch(`${API}/settings/payment`)
        ]);
        
        if (venueRes.ok) {
          const venueData = await venueRes.json();
          console.log('Venue data loaded:', venueData);
          console.log('Available courts:', venueData.courts);
          setVenue(venueData);
          if (!selectedCourt && venueData.courts?.length > 0) {
            setSelectedCourt(venueData.courts[0].court_id);
          }
        }
        
        if (addonsRes.ok) {
          setAddons(await addonsRes.json());
        }
        
        if (venueInfoRes.ok) {
          setVenueInfo(await venueInfoRes.json());
        }
        
        if (paymentSettingsRes.ok) {
          const paymentSettings = await paymentSettingsRes.json();
          // setEnableMockPayment(paymentSettings.enable_mock_payment);
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to load booking data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [venueId, selectedCourt]);

  // Stable ref for the selected date to use in WebSocket handler
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);

  // Stable refs for fetch callbacks — used by long-lived effects (WebSocket, polling)
  // so they always invoke the latest version without causing effect re-runs.
  const fetchAvailabilityRef = useRef(fetchAvailability);
  useEffect(() => { fetchAvailabilityRef.current = fetchAvailability; }, [fetchAvailability]);
  const fetchKioskTransactionsRef = useRef(fetchKioskTransactions);
  useEffect(() => { fetchKioskTransactionsRef.current = fetchKioskTransactions; }, [fetchKioskTransactions]);

  // ======================== REALTIME WEBSOCKET + POLLING FALLBACK ========================
  // Listen for booking/block-off events from other devices/admins and refresh availability.
  // If WebSocket is unavailable (e.g., nginx not configured for WS), fall back to polling.
  useEffect(() => {
    let ws = null;
    let reconnectTimer;
    let reconnectDelayMs = 2000;
    let wsConnected = false;
    let pollTimer = null;
    const POLL_INTERVAL_MS = 30000; // Poll every 30 seconds when WS is down

    // Start polling as fallback when WS is not connected
    const startPolling = () => {
      if (pollTimer) return; // Already polling
      pollTimer = setInterval(() => {
        if (!wsConnected) {
          fetchAvailabilityRef.current();
          fetchKioskTransactionsRef.current();
        }
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };
    
    const connect = () => {
      try {
        const wsUrl = getWebSocketUrl();
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          wsConnected = true;
          reconnectDelayMs = 2000;
          stopPolling(); // WS is live, no need to poll
          // Refresh once on connect to pick up anything missed while disconnected
          fetchAvailabilityRef.current();
          fetchKioskTransactionsRef.current();
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Refresh availability on any booking change event
            if (['booking_created', 'booking_updated', 'booking_deleted',
                 'booking_cancelled', 'block_off_created', 'block_off_deleted',
                 'payment_confirmed', 'kiosk_transaction_updated'].includes(data.type)) {
              fetchAvailabilityRef.current();
              fetchKioskTransactionsRef.current();
            }
          } catch (error) {
            // Ignore parse errors
          }
        };
        
        ws.onerror = () => {
          wsConnected = false;
          startPolling(); // WS failed, fall back to polling
        };
        
        ws.onclose = () => {
          wsConnected = false;
          startPolling(); // WS closed, fall back to polling
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
            connect();
          }, reconnectDelayMs);
        };
      } catch (error) {
        // WebSocket not available — fall back to polling
        wsConnected = false;
        startPolling();
      }
    };

    connect();
    // Also start polling immediately as safety net until WS connects
    startPolling();

    return () => {
      stopPolling();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, []); // Stable — uses refs for callbacks, no need to teardown on date/court change

  // Sync cart items to selectedAddons when component mounts or cart changes
  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      setSelectedAddons(prev => {
        // Merge cart items with any manually selected addons
        const merged = [...prev];
        cartItems.forEach(cartItem => {
          const existingIndex = merged.findIndex(a => a.item_id === cartItem.item_id);
          if (existingIndex === -1) {
            // Add cart item to selected addons
            merged.push({
              item_id: cartItem.item_id,
              name: cartItem.name,
              price: cartItem.price,
              quantity: cartItem.quantity,
              image_url: cartItem.image_url,
              fromCart: true // Mark as coming from cart
            });
          } else {
            // Update quantity if item already exists
            merged[existingIndex].quantity = Math.max(merged[existingIndex].quantity, cartItem.quantity);
            merged[existingIndex].fromCart = true;
          }
        });
        return merged;
      });
    }
  }, [cartItems]);

  const handleSlotToggle = (slotTime, isAvailable) => {
    if (!isAvailable) return;
    
    // Block toggling while availability is being fetched
    if (fetchingAvailability) {
      toast.info('Loading availability, please wait...');
      return;
    }
    
    // Verify the availability version matches current date+court
    const expectedVersion = `${format(selectedDate, 'yyyy-MM-dd')}|${selectedCourt}`;
    if (availabilityVersion !== expectedVersion) {
      toast.error('Slot data is out of date. Refreshing...');
      fetchAvailability();
      return;
    }
    
    // Re-verify availability against current state to prevent stale clicks
    const currentSlotInfo = availability.find(
      s => s.court_id === selectedCourt && s.start_time === slotTime
    );
    if (currentSlotInfo && !currentSlotInfo.available) {
      toast.error('This slot is no longer available. Refreshing...');
      fetchAvailability();
      return;
    }
    
    // Also check kiosk reservations
    if (isSlotKioskReserved(selectedCourt, slotTime)) {
      toast.error('This slot is reserved via kiosk.');
      return;
    }
    
    setSelectedSlots(prev => {
      const isSelected = prev.includes(slotTime);
      if (isSelected) {
        // Remove slot
        return prev.filter(s => s !== slotTime);
      } else {
        // Add slot and sort by time
        const newSlots = [...prev, slotTime].sort((a, b) => {
          const hourA = parseInt(a.split(':')[0]);
          const hourB = parseInt(b.split(':')[0]);
          return hourA - hourB;
        });
        return newSlots;
      }
    });
  };

  // Get formatted time slots for display
  const getFormattedTimeSlots = () => {
    if (selectedSlots.length === 0) return [];
    return [...selectedSlots].sort((a, b) => parseInt(a.split(':')[0]) - parseInt(b.split(':')[0]));
  };

  // Get display string for selected time slots
  const getTimeSlotsDisplay = () => {
    const sorted = getFormattedTimeSlots();
    if (sorted.length === 0) return '';
    
    // Group consecutive slots into ranges
    const ranges = [];
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];
    
    for (let i = 1; i < sorted.length; i++) {
      const prevHour = parseInt(rangeEnd.split(':')[0]);
      const currHour = parseInt(sorted[i].split(':')[0]);
      
      if (currHour - prevHour === 1) {
        rangeEnd = sorted[i];
      } else {
        // End current range and start new one
        const endHour = parseInt(rangeEnd.split(':')[0]) + 1;
        ranges.push(`${formatTimeToAMPM(rangeStart)} - ${formatTimeToAMPM(`${endHour.toString().padStart(2, '0')}:00`)}`);
        rangeStart = sorted[i];
        rangeEnd = sorted[i];
      }
    }
    // Add last range
    const endHour = parseInt(rangeEnd.split(':')[0]) + 1;
    ranges.push(`${formatTimeToAMPM(rangeStart)} - ${formatTimeToAMPM(`${endHour.toString().padStart(2, '0')}:00`)}`);
    
    return ranges.join(', ');
  };

  const handleAddonToggle = (addon) => {
    setSelectedAddons(prev => {
      const existing = prev.find(e => e.item_id === addon.item_id);
      if (existing) {
        return prev.filter(e => e.item_id !== addon.item_id);
      }
      return [...prev, { ...addon, quantity: 1 }];
    });
  };

  const updateAddonQuantity = (itemId, delta) => {
    setSelectedAddons(prev => {
      return prev.map(addon => {
        if (addon.item_id === itemId) {
          const newQty = Math.max(1, addon.quantity + delta);
          return { ...addon, quantity: newQty };
        }
        return addon;
      });
    });
  };

  const calculateCourtCost = () => {
    if (!venue || selectedSlots.length === 0) return 0;
    
    // Calculate court cost using actual slot prices from pricing rules
    return selectedSlots.reduce((sum, slotTime) => {
      const slot = courtSlots.find(s => s.start_time === slotTime);
      return sum + (slot?.price || venue.price_per_hour);
    }, 0);
  };

  const calculateTotal = () => {
    // If test amount override is enabled, return the test amount
    // if (useTestAmount && testAmount > 0) {
    //   return Math.round(testAmount);
    // }
    
    if (!venue || selectedSlots.length === 0) return 0;
    
    const courtCost = calculateCourtCost();
    
    // Add-ons are charged per item (not per hour)
    const addonsCost = selectedAddons.reduce((sum, addon) => sum + (addon.price * addon.quantity), 0);
    
    let total = courtCost + addonsCost;
    
    // Apply membership discount
    if (user?.membership_type) {
      const discounts = { 'mem_basic': 0.1, 'mem_pro': 0.2, 'mem_elite': 0.3 };
      total *= (1 - (discounts[user.membership_type] || 0));
    }
    
    return Math.round(total);
  };

  // Show payment confirmation dialog
  const handleBookingClick = async (useMock = false) => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }

    // Validate phone number for e-wallet payments
    if (isEwalletSelected) {
      if (!ewalletPhone) {
        toast.error('Please enter your mobile number for e-wallet payment');
        return;
      }
      if (!isValidPhoneNumber(ewalletPhone)) {
        toast.error('Please enter a valid Philippine mobile number (09XXXXXXXXX)');
        return;
      }
    }

    if (!numPlayers || numPlayers < 2 || numPlayers > 8) {
      toast.error('Please enter a valid number of players (2-8)');
      return;
    }

    // Re-validate availability before showing confirmation (protects slow connections)
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(`${API}/venues/${venueId}/availability?date=${dateStr}`);

      if (response.ok) {
        const data = await response.json();
        const freshSlots = data.slots || [];
        const unavailable = selectedSlots.filter(slotTime => {
          const slotInfo = freshSlots.find(s => s.court_id === selectedCourt && s.start_time === slotTime);
          return slotInfo && !slotInfo.available;
        });
        if (unavailable.length > 0) {
          setAvailability(freshSlots);
          setSelectedSlots(prev => prev.filter(s => !unavailable.includes(s)));
          toast.error(`${unavailable.length} slot(s) are no longer available. They may have been booked by someone else.`);
          return;
        }
        setAvailability(freshSlots);
      }
    } catch (error) {
      // If recheck fails, proceed anyway — backend will still validate
    }

    // Set whether to use mock payment
    setUseMockPayment(useMock);
    // Show confirmation dialog
    setShowPaymentConfirmation(true);
  };

  // Show reserve confirmation dialog (pay at counter)
  const handleReserveClick = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }

    if (!numPlayers || numPlayers < 2 || numPlayers > 8) {
      toast.error('Please enter a valid number of players (2-8)');
      return;
    }

    // Re-validate availability before showing confirmation (protects slow connections)
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(`${API}/venues/${venueId}/availability?date=${dateStr}`);

      if (response.ok) {
        const data = await response.json();
        const freshSlots = data.slots || [];
        const unavailable = selectedSlots.filter(slotTime => {
          const slotInfo = freshSlots.find(s => s.court_id === selectedCourt && s.start_time === slotTime);
          return slotInfo && !slotInfo.available;
        });
        if (unavailable.length > 0) {
          setAvailability(freshSlots);
          setSelectedSlots(prev => prev.filter(s => !unavailable.includes(s)));
          toast.error(`${unavailable.length} slot(s) are no longer available. They may have been booked by someone else.`);
          return;
        }
        setAvailability(freshSlots);
      }
    } catch (error) {
      // If recheck fails, proceed anyway — backend will still validate
    }

    setShowReserveConfirmation(true);
  };

  // Process reserve only (pay at counter)
  const handleReserve = async () => {
    setShowReserveConfirmation(false);
    setProcessingReserve(true);
    setSubmitting(true);

    const sortedSlots = getFormattedTimeSlots();
    const totalAmount = calculateTotal();

    try {
      console.log('Creating reservation...');
      const reserveResponse = await fetch(`${API}/bookings/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          venue_id: venueId,
          court_id: selectedCourt,
          date: format(selectedDate, 'yyyy-MM-dd'),
          time_slots: sortedSlots,
          duration_hours: duration,
          num_players: numPlayers,
          equipment_rentals: selectedAddons.map(addon => ({
            item_id: addon.item_id,
            name: addon.name,
            quantity: addon.quantity,
            price: addon.price
          })),
          payment_method: 'cash',
          total_amount: totalAmount
        })
      });

      let reserveResult;
      try {
        reserveResult = await reserveResponse.json();
      } catch (jsonError) {
        console.error('Failed to parse reserve response:', jsonError);
        throw new Error('Invalid response from server');
      }
      
      if (!reserveResponse.ok) {
        console.error('Reservation failed:', reserveResult);
        // Handle slot conflict (409) — auto-refresh availability
        if (reserveResponse.status === 409) {
          toast.error(reserveResult.detail || 'One or more selected slots are no longer available. Refreshing...');
          setSelectedSlots([]);
          fetchAvailability();
          return;
        }
        throw new Error(reserveResult.detail || 'Failed to create reservation');
      }

      console.log('Reservation created:', reserveResult);
      toast.success('Reservation confirmed! Please pay at the counter on your scheduled date.');
      
      // Clear cart items after successful booking
      clearCart();
      
      // Navigate to a success page or dashboard
      navigate('/dashboard', { 
        state: { 
          bookingSuccess: true, 
          bookingId: reserveResult.booking.booking_id,
          message: 'Your reservation has been confirmed. Please pay at the counter.'
        } 
      });
    } catch (error) {
      console.error('Reservation error:', error);
      toast.error(error.message || 'Failed to create reservation');
    } finally {
      setProcessingReserve(false);
      setSubmitting(false);
    }
  };

  // Process payment after confirmation
  const handleBooking = async () => {
    setShowPaymentConfirmation(false);
    setProcessingPayment(true);
    setSubmitting(true);

    const sortedSlots = getFormattedTimeSlots();
    const totalAmount = calculateTotal();

    try {
      // Step 1: Create a pending booking first
      console.log('Initiating booking...');
      const bookingResponse = await fetch(`${API}/bookings/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          venue_id: venueId,
          court_id: selectedCourt,
          date: format(selectedDate, 'yyyy-MM-dd'),
          time_slots: sortedSlots,
          duration_hours: duration,
          num_players: numPlayers,
          equipment_rentals: selectedAddons.map(addon => ({
            item_id: addon.item_id,
            name: addon.name,
            quantity: addon.quantity,
            price: addon.price
          })),
          payment_method: selectedPaymentMethod,
          total_amount: totalAmount
        })
      });

      let bookingResult;
      try {
        bookingResult = await bookingResponse.json();
      } catch (jsonError) {
        console.error('Failed to parse booking response:', jsonError);
        throw new Error('Invalid response from server');
      }
      
      if (!bookingResponse.ok) {
        console.error('Booking initiation failed:', bookingResult);
        // Handle slot conflict (409) — auto-refresh availability
        if (bookingResponse.status === 409) {
          toast.error(bookingResult.detail || 'One or more selected slots are no longer available. Refreshing...');
          setSelectedSlots([]);
          fetchAvailability();
          return;
        }
        throw new Error(bookingResult.detail || 'Failed to initiate booking');
      }

      console.log('Booking created:', bookingResult);
      const bookingData = bookingResult;

      // Step 2: Create payment link based on payment method
      let paymentResult;
      
      if (isEwalletSelected) {
        // Use CamSur Payments API for e-wallet payments
        console.log(`Creating e-wallet payment for ${selectedPaymentMethod}...`);
        const paymentResponse = await fetch(`${API}/payments/ewallet`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            booking_id: bookingData.booking.booking_id,
            amount: totalAmount,
            payment_method: selectedPaymentMethod,
            phone_number: ewalletPhone
          })
        });

        try {
          paymentResult = await paymentResponse.json();
        } catch (jsonError) {
          console.error('Failed to parse e-wallet payment response:', jsonError);
          throw new Error('Invalid response from payment service');
        }
        
        if (!paymentResponse.ok) {
          console.error('E-wallet payment creation failed:', paymentResult);
          throw new Error(paymentResult.detail || 'Failed to create e-wallet payment');
        }
      } else {
        // Use UnionBank/legacy API for other payment methods
        console.log(`Creating payment link (${useMockPayment ? 'MOCK' : 'LIVE'})...`);
        const paymentResponse = await fetch(`${API}/payments/create-link`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            booking_id: bookingData.booking.booking_id,
            amount: totalAmount,
            payment_method: selectedPaymentMethod,
            use_mock: useMockPayment
          })
        });

        try {
          paymentResult = await paymentResponse.json();
        } catch (jsonError) {
          console.error('Failed to parse payment response:', jsonError);
          throw new Error('Invalid response from payment service');
        }
        
        if (!paymentResponse.ok) {
          console.error('Payment link creation failed:', paymentResult);
          throw new Error(paymentResult.detail || 'Failed to create payment link');
        }
      }

      console.log('Payment link created:', paymentResult);
      const paymentData = paymentResult;

      // Step 3: Redirect to payment page
      if (paymentData.payment_url) {
        toast.info('Redirecting to payment page...');
        // Store booking ID in sessionStorage for reference after payment
        sessionStorage.setItem('pending_booking_id', bookingData.booking.booking_id);
        sessionStorage.setItem('pending_transaction_id', paymentData.transaction_id);
        // Clear cart items before payment redirect
        clearCart();
        window.location.href = paymentData.payment_url;
      } else {
        throw new Error('No payment URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
      setSubmitting(false);
    }
  };

  // Derive court slots — only show when data is fresh
  const courtSlots = useMemo(() => {
    const expectedVersion = selectedDate && selectedCourt
      ? `${format(selectedDate, 'yyyy-MM-dd')}|${selectedCourt}`
      : '';

    // If version doesn't match, data is stale or still loading — show nothing
    if (!availabilityVersion || availabilityVersion !== expectedVersion) {
      return [];
    }

    return availability.filter(slot => slot.court_id === selectedCourt);
  }, [availability, selectedCourt, selectedDate, availabilityVersion]);
  const selectedCourtInfo = venue?.courts?.find(c => c.court_id === selectedCourt);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="h-8 bg-muted animate-pulse rounded w-1/3 mb-8"></div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="h-96 bg-muted animate-pulse rounded-xl"></div>
            <div className="h-96 bg-muted animate-pulse rounded-xl"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="booking-page">
        {/* Header with Venue Info */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Book a Court</h1>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-muted-foreground">
            <p className="flex items-center">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
              <span>{venueInfo?.name || venue?.name}</span>
            </p>
            {venueInfo?.address && (
              <p className="text-sm hidden sm:block">• {venueInfo.address}</p>
            )}
            {venueInfo?.contact_number && (
              <p className="flex items-center text-sm">
                <Phone className="h-3 w-3 mr-1" />
                {venueInfo.contact_number}
              </p>
            )}
          </div>
          
          {/* Operating Hours & Links */}
          {venueInfo && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              {/* Operating Hours */}
              <div className="flex items-center gap-1 bg-muted px-3 py-1 rounded-full">
                <Clock className="h-3 w-3" />
                <span>
                  {(() => {
                    const isWeekend = [0, 6].includes(selectedDate.getDay());
                    const open = isWeekend ? venueInfo.weekend_open : venueInfo.weekday_open;
                    const close = isWeekend ? venueInfo.weekend_close : venueInfo.weekday_close;
                    if (!open || !close) return 'Hours not set';
                    const formatHour = (t) => {
                      const h = parseInt(t.split(':')[0]);
                      return h === 0 ? '12AM' : h === 12 ? '12PM' : h > 12 ? `${h-12}PM` : `${h}AM`;
                    };
                    return `${formatHour(open)} - ${formatHour(close)}`;
                  })()}
                </span>
              </div>
              
              {/* Quick Links */}
              <div className="flex items-center gap-2">
                {venueInfo.google_maps_url && (
                  <a 
                    href={venueInfo.google_maps_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <MapPin className="h-3 w-3" />
                    <span className="hidden sm:inline">Google Maps</span>
                    <span className="sm:hidden">Map</span>
                  </a>
                )}
                {venueInfo.waze_url && (
                  <a 
                    href={venueInfo.waze_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Waze
                  </a>
                )}
              </div>
              
              {/* Social Links */}
              <div className="flex items-center gap-2 ml-auto">
                {venueInfo.facebook_url && (
                  <a href={venueInfo.facebook_url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600" title="Facebook">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                )}
                {venueInfo.instagram_url && (
                  <a href={venueInfo.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:text-pink-600" title="Instagram">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  </a>
                )}
                {venueInfo.tiktok_url && (
                  <a href={venueInfo.tiktok_url} target="_blank" rel="noopener noreferrer" className="hover:text-gray-900" title="TikTok">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
                  </a>
                )}
                {venueInfo.youtube_url && (
                  <a href={venueInfo.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:text-red-600" title="YouTube">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step >= s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                }`}
              >
                {step > s ? <CheckCircle className="h-5 w-5" /> : s}
              </div>
              {s < 3 && <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-primary' : 'bg-muted'}`}></div>}
            </React.Fragment>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {step === 1 && (
              <>
                {/* Date Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle>Select Date</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      disabled={(date) => isBefore(date, startOfDay(new Date())) || date > addDays(new Date(), 30)}
                      className="rounded-md border mx-auto"
                      data-testid="booking-calendar"
                    />
                  </CardContent>
                </Card>

                {/* Court Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle>Select Court</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedCourt} onValueChange={setSelectedCourt}>
                      <SelectTrigger data-testid="court-select">
                        <SelectValue placeholder="Choose a court" />
                      </SelectTrigger>
                      <SelectContent>
                        {venue?.courts?.map((court) => (
                          <SelectItem key={court.court_id} value={court.court_id}>
                            {court.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Number of Players Input */}
                    <div className="mt-4 space-y-2">
                      <Label htmlFor="num-players">Number of Players</Label>
                      <Input
                        id="num-players"
                        type="number"
                        min="2"
                        max="8"
                        value={numPlayers}
                        onChange={(e) => setNumPlayers(Math.max(2, Math.min(8, parseInt(e.target.value) || 2)))}
                        required
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum 8 players allowed per court.
                      </p>
                    </div>
                    
                    {selectedCourtInfo && (
                      <div className="mt-4 flex gap-2">
                        {selectedCourtInfo.indoor && <Badge variant="outline">Indoor</Badge>}
                        {selectedCourtInfo.lighting && <Badge variant="outline">Lighting</Badge>}
                        <Badge variant="outline">{selectedCourtInfo.court_type}</Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Time Slots */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Available Time Slots</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          Select one or more consecutive time slots
                          {isToday(selectedDate) && (
                            <span className="block text-xs mt-1 text-orange-500">
                              Note: Past time slots are unavailable
                            </span>
                          )}
                        </p>
                      </div>
                      {selectedSlots.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedSlots([])}
                          className="text-destructive border-destructive/50 hover:bg-destructive/10"
                          data-testid="clear-all-slots"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Clear All
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mb-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded border-2 border-primary bg-transparent"></div>
                        <span className="text-muted-foreground">Available</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded bg-primary"></div>
                        <span className="text-muted-foreground">Selected</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded bg-slate-400/50"></div>
                        <span className="text-muted-foreground">Already Booked</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded bg-amber-500/50 border border-amber-600"></div>
                        <span className="text-muted-foreground">Kiosk Reserved</span>
                      </div>
                      {isToday(selectedDate) && (
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded bg-amber-500/30 border border-amber-500/50"></div>
                          <span className="text-muted-foreground">Past</span>
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      {/* Loading overlay prevents interaction with stale slot data */}
                      {fetchingAvailability && (
                        <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-md">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            <span className="text-sm">Refreshing availability...</span>
                          </div>
                        </div>
                      )}
                      <div className={`grid grid-cols-4 sm:grid-cols-6 gap-2 ${fetchingAvailability ? 'pointer-events-none opacity-50' : ''}`}>
                        {courtSlots.map((slot, idx) => {
                          const isSelected = selectedSlots.includes(slot.start_time);
                          const isPast = isSlotPassed(slot.start_time);
                          const isBooked = !slot.available && !isPast;
                          const isKioskReserved = isSlotKioskReserved(selectedCourt, slot.start_time);
                          const kioskTransaction = getKioskTransactionForSlot(selectedCourt, slot.start_time);
                          const isDisabled = !slot.available || isPast || isKioskReserved || fetchingAvailability;
                          
                          return (
                            <Button
                              key={`${slot.court_id}-${slot.start_time}`}
                              variant={isSelected ? 'default' : 'outline'}
                              className={`relative ${
                                isSelected ? 'bg-primary ring-2 ring-primary ring-offset-2' : ''
                              } ${
                                isPast && slot.available 
                                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-600 line-through opacity-60' 
                                  : ''
                              } ${
                                isBooked 
                                  ? 'bg-slate-400/30 border-slate-400/50 text-slate-500 opacity-60' 
                                  : ''
                              } ${
                                isKioskReserved && !isBooked && !isPast
                                  ? 'bg-amber-500/30 border-amber-600 text-amber-700 hover:bg-amber-500/40' 
                                  : ''
                              } ${
                                isDisabled && !isKioskReserved ? 'cursor-not-allowed' : ''
                              } ${
                                isKioskReserved ? 'cursor-pointer' : ''
                              }`}
                              disabled={isDisabled && !isKioskReserved}
                              onClick={() => {
                                if (fetchingAvailability) return;
                                if (isKioskReserved && kioskTransaction) {
                                  handleKioskTransactionClick(kioskTransaction);
                                } else if (!isDisabled) {
                                  handleSlotToggle(slot.start_time, !isDisabled);
                                }
                              }}
                              data-testid={`slot-${slot.start_time}`}
                              title={
                                fetchingAvailability ? 'Loading...' :
                                isPast ? 'This time has passed' : 
                                isKioskReserved ? 'Kiosk Reserved - Click to view details' :
                                isBooked ? 'Already booked' : 'Available'
                              }
                            >
                              {isKioskReserved && (
                                <Ticket className="h-3 w-3 absolute top-0.5 right-0.5 text-amber-600" />
                              )}
                              {formatTimeToAMPM(slot.start_time)}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                    {courtSlots.length === 0 && !fetchingAvailability && (
                      <p className="text-center text-muted-foreground py-4">No slots available for this date</p>
                    )}
                  </CardContent>
                </Card>

                {/* Selected Slots Summary */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Selected Time Slots</CardTitle>
                      {selectedSlots.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedSlots([])}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedSlots.length > 0 ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {selectedSlots.sort((a, b) => parseInt(a.split(':')[0]) - parseInt(b.split(':')[0])).map((slot) => (
                            <Badge 
                              key={slot} 
                              className="bg-primary text-white px-3 py-1"
                            >
                              {formatTimeToAMPM(slot)}
                            </Badge>
                          ))}
                        </div>
                        <div className="pt-2 border-t">
                          <p className="text-sm">
                            <span className="font-medium">Duration:</span> {duration} hour{duration > 1 ? 's' : ''}
                          </p>
                          {selectedSlots.length > 0 && (
                            <p className="text-sm">
                              <span className="font-medium">Time:</span> {getTimeSlotsDisplay()}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Click on available time slots above to select</p>
                    )}
                  </CardContent>
                </Card>

                <Button 
                  onClick={() => setStep(2)} 
                  className="w-full btn-primary"
                  disabled={selectedSlots.length === 0 || fetchingAvailability}
                  data-testid="continue-to-equipment"
                >
                  {fetchingAvailability ? 'Loading...' : 'Continue'}
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                {/* Cart Items Notification */}
                {cartItems && cartItems.length > 0 && (
                  <Card className="border-green-500 bg-green-50 dark:border-green-600  dark:bg-slate-900 dark:text-green-200">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-200">
                        <ShoppingCart className="h-5 w-5" />
                        <span className="font-medium">
                          {cartItems.length} item{cartItems.length > 1 ? 's' : ''} from your cart added to this booking!
                        </span>
                      </div>
                      <p className="text-sm text-green-600 dark:text-green-200 mt-1">
                        The items you added to your cart have been automatically included below. You can adjust quantities or remove them.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Add-ons */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <ShoppingCart className="h-5 w-5 mr-2" />
                      Add-ons (Optional)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {addons.length === 0 ? (
                        <p className="text-muted-foreground text-sm">No add-ons available</p>
                      ) : (
                        addons.map((addon) => {
                          const selectedAddon = selectedAddons.find(e => e.item_id === addon.item_id);
                          const isSelected = !!selectedAddon;
                          const isFromCart = selectedAddon?.fromCart;
                          
                          return (
                            <div 
                              key={addon.item_id} 
                              className={`p-4 border rounded-lg transition-colors ${
                                isSelected 
                                  ? isFromCart 
                                    ? 'border-green-500 bg-green-50 dark:border-green-600 dark:bg-black dark:text-green-200' 
                                    : 'border-primary bg-primary/5' 
                                  : ''
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3 flex-1">
                                  <Checkbox
                                    id={addon.item_id}
                                    checked={isSelected}
                                    onCheckedChange={() => handleAddonToggle(addon)}
                                    data-testid={`addon-${addon.item_id}`}
                                  />
                                  <div className="flex items-center gap-3 flex-1">
                                    {addon.image_url && (
                                      <img 
                                        src={addon.image_url} 
                                        alt={addon.name}
                                        className="w-12 h-12 object-cover rounded-lg"
                                      />
                                    )}
                                    <Label htmlFor={addon.item_id} className="cursor-pointer flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{addon.name}</span>
                                        {isFromCart && (
                                          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full dark:bg-green-400 dark:text-slate-900">
                                            From Cart
                                          </span>
                                        )}
                                      </div>
                                      {addon.description && (
                                        <span className="text-sm text-muted-foreground block dark:text-slate-300">{addon.description}</span>
                                      )}
                                    </Label>
                                  </div>
                                </div>
                                <span className="font-medium text-primary">{formatCurrency(addon.price)}</span>
                              </div>
                              
                              {/* Quantity Controls - shown when selected */}
                              {isSelected && (
                                <div className="mt-3 pt-3 border-t flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground dark:text-slate-300">Quantity:</span>
                                  <div className="flex items-center gap-3">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8 rounded-full dark:border-slate-700 dark:bg-transparent"
                                      onClick={() => updateAddonQuantity(addon.item_id, -1)}
                                      disabled={selectedAddon.quantity <= 1}
                                      data-testid={`addon-${addon.item_id}-decrease`}
                                    >
                                      <Minus className="h-4 w-4" />
                                    </Button>
                                    <span className="w-8 text-center font-semibold text-white dark:text-white" data-testid={`addon-${addon.item_id}-qty`}>
                                      {selectedAddon.quantity}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8 rounded-full dark:border-slate-700 dark:bg-transparent"
                                      onClick={() => updateAddonQuantity(addon.item_id, 1)}
                                      data-testid={`addon-${addon.item_id}-increase`}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </Button>
                                    <span className="text-sm font-medium ml-2">
                                      = {formatCurrency(addon.price * selectedAddon.quantity)}
                                    </span>
                                    {isFromCart && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-800 dark:text-red-400"
                                        onClick={() => {
                                          handleAddonToggle(addon);
                                          removeFromCart(addon.item_id);
                                        }}
                                        title="Remove from cart"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button onClick={() => setStep(3)} className="flex-1 btn-primary" data-testid="continue-to-payment">
                    Continue to Payment
                  </Button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                {/* Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Booking Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Venue</span>
                      <span className="font-medium">{venue?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Court</span>
                      <span className="font-medium">{selectedCourtInfo?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium">{format(selectedDate, 'MMMM d, yyyy')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Time</span>
                      <span className="font-medium">{getTimeSlotsDisplay()} ({duration}hr{duration > 1 ? 's' : ''})</span>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Court Fee</span>
                      <span>{formatCurrency(calculateCourtCost())}</span>
                    </div>
                    
                    {selectedAddons.length > 0 && (
                      <>
                        <div className="text-sm font-medium">Add-ons</div>
                        {selectedAddons.map((addon) => (
                          <div key={addon.item_id} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{addon.name} x{addon.quantity}</span>
                            <span>{formatCurrency(addon.price * addon.quantity)}</span>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {user?.membership_type && (
                      <div className="flex justify-between text-green-600">
                        <span>Membership Discount</span>
                        <span>Applied</span>
                      </div>
                    )}
                    
                    <Separator />
                    
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total</span>
                      <span className="text-primary">{formatCurrency(calculateTotal())}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Method Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Select Payment Method
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-black dark:text-white mb-4">
                      Choose your preferred e-wallet for faster checkout, or select "Debit/Credit Card" to pay with card.
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
                      {paymentMethodOptions.map((method) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => setSelectedPaymentMethod(method.id)}
                          className={`flex flex-col items-center justify-center p-3 border rounded-lg transition-all ${
                            selectedPaymentMethod === method.id
                              ? 'border-primary bg-primary/10 dark:bg-primary/20 ring-2 ring-primary'
                              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary/50'
                          }`}
                        >
                          <img 
                            src={method.src} 
                            alt={method.alt}
                            className="h-10  object-contain mb-1"
                          />
                          <span className="text-xs font-medium text-foreground">{method.label}</span>
                        </button>
                      ))}
                    </div>
                    
                    {/* Phone number input for e-wallet payments */}
                    {isEwalletSelected && (
                      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Mobile Number for {paymentMethodOptions.find(m => m.id === selectedPaymentMethod)?.label}
                        </label>
                        <input
                          type="tel"
                          value={ewalletPhone}
                          onChange={(e) => setEwalletPhone(e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="09XXXXXXXXX"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                          maxLength={11}
                        />
                        <p className="text-xs text-foreground mt-1">
                          Enter the mobile number linked to your {paymentMethodOptions.find(m => m.id === selectedPaymentMethod)?.label} account
                        </p>
                        {ewalletPhone && !isValidPhoneNumber(ewalletPhone) && (
                          <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                            Please enter a valid Philippine mobile number (09XXXXXXXXX)
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Legacy payment methods (displayed elsewhere when needed) */}
                  </CardContent>
                </Card>

                {/* Payment Warning Notice */}
                <Card className="border-amber-500 bg-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          Payment Notice
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          By clicking "Proceed to Payment", you will be redirected to our secure payment partner. 
                          The total amount of <strong>{formatCurrency(calculateTotal())}</strong> will be automatically 
                          deducted from your chosen payment method upon confirmation.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>


                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                    Back
                  </Button>
                   {/* {enableMockPayment && (
                    <Button 
                      onClick={() => handleBookingClick(true)} 
                      variant="outline"
                      className="flex-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                      disabled={submitting || processingPayment || processingReserve}
                      data-testid="mock-payment"
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Mock Payment
                        </>
                      )}
                    </Button>
                  )} */}
                  
                  <Button 
                    onClick={() => handleBookingClick(false)} 
                    className="flex-1 btn-primary"
                    disabled={submitting || processingPayment || processingReserve}
                    data-testid="confirm-booking"
                  >
                    {processingPayment ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Shield className="h-4 w-4 mr-2" />
                        Proceed to Payment
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="hidden lg:block">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h3 className="font-heading font-bold mb-4">Your Booking</h3>
                
                {selectedSlots.length > 0 ? (
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{format(selectedDate, 'MMM d')} • {getTimeSlotsDisplay()}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{selectedCourtInfo?.name || 'Select court'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {duration} hour{duration > 1 ? 's' : ''} • {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} selected
                    </p>
                    
                    <Separator className="my-4" />
                    
                    <div className="flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-primary">{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Select time slots to see booking details</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Kiosk Transaction Modal */}
      <Dialog open={showKioskModal} onOpenChange={closeKioskModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-amber-500" />
              Kiosk Reservation
            </DialogTitle>
          </DialogHeader>
          
          {kioskModalLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2">Loading transaction details...</span>
            </div>
          ) : selectedKioskTransaction ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-amber-800">Pending Payment</span>
                </div>
                <p className="text-sm text-amber-700">
                  This slot is reserved via kiosk and awaiting payment completion.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transaction Code:</span>
                  <span className="font-mono font-bold">{selectedKioskTransaction.transaction_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Court:</span>
                  <span>{selectedKioskTransaction.court_name || `Court ${selectedKioskTransaction.court_id}`}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span>{selectedKioskTransaction.booking_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time:</span>
                  <span>{selectedKioskTransaction.start_time} - {selectedKioskTransaction.end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration:</span>
                  <span>{selectedKioskTransaction.duration || 1} hour(s)</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-bold text-primary">{formatCurrency(selectedKioskTransaction.amount || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                    {selectedKioskTransaction.status || 'pending'}
                  </Badge>
                </div>
                {selectedKioskTransaction.created_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created:</span>
                    <span className="text-sm">{new Date(selectedKioskTransaction.created_at).toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="bg-muted/50 rounded-lg p-3 mt-4">
                <p className="text-xs text-muted-foreground text-center">
                  This reservation will expire if payment is not completed at the kiosk.
                  Please check back later if you need this slot.
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No transaction details available
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={closeKioskModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <AlertDialog open={showPaymentConfirmation} onOpenChange={setShowPaymentConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {/* {useMockPayment ? 'Mock Payment (Test Mode)' : 'Confirm Payment'} */}
              Confirm Payment
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {/* {useMockPayment && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-800">
                      <strong>⚠️ Test Mode:</strong> This is a mock payment for testing purposes only. No real payment will be processed.
                    </p>
                  </div>
                )} */}
                <p>
                  You are about to proceed with the payment for your booking. Please review the details below:
                </p>
                
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Court:</span>
                    <span className="font-medium">{selectedCourtInfo?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Date:</span>
                    <span className="font-medium">{format(selectedDate, 'MMMM d, yyyy')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Time:</span>
                    <span className="font-medium">{getTimeSlotsDisplay()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Players:</span>
                    <span className="font-medium">{numPlayers}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-bold">
                    <span>Total Amount:</span>
                    <span className="text-primary">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>

                {/* Test Amount Override - for production gateway testing */}
                {/* <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="useTestAmount"
                      checked={useTestAmount}
                      onChange={(e) => setUseTestAmount(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="useTestAmount" className="text-sm font-medium text-yellow-800">
                      Override amount for testing
                    </label>
                  </div>
                  {useTestAmount && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-yellow-700">Test Amount (PHP):</span>
                      <input
                        type="number"
                        min="1"
                        value={testAmount}
                        onChange={(e) => setTestAmount(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-24 px-2 py-1 text-sm border border-yellow-400 rounded bg-white text-yellow-900"
                      />
                    </div>
                  )}
                </div> */}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      <strong>Time Sensitive:</strong> You have 5 minutes to complete your payment transaction or your booking will be cancelled.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> By clicking "Confirm & Pay", you agree that the amount of{' '}
                      <strong>{formatCurrency(calculateTotal())}</strong> will be automatically deducted from 
                      your selected payment method. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingPayment}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBooking}
              disabled={processingPayment}
              className="bg-primary hover:bg-primary/90"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {useMockPayment ? 'Test Payment' : 'Confirm & Pay'} {formatCurrency(calculateTotal())}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reserve Confirmation Dialog */}
      <AlertDialog open={showReserveConfirmation} onOpenChange={setShowReserveConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Confirm Reservation
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  You are about to reserve a court slot. Payment will be collected at the counter on your scheduled date.
                </p>
                
                <div className="bg-muted rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Court:</span>
                    <span className="font-medium">{selectedCourtInfo?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Date:</span>
                    <span className="font-medium">{format(selectedDate, 'MMMM d, yyyy')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Time:</span>
                    <span className="font-medium">{getTimeSlotsDisplay()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Players:</span>
                    <span className="font-medium">{numPlayers}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-bold">
                    <span>Total Amount (Pay at Counter):</span>
                    <span className="text-blue-600">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      <strong>Important:</strong> Please arrive at least 15 minutes before your scheduled time to complete payment at the counter.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Unpaid reservations may be released to other customers if not claimed within 15 minutes of the scheduled time.
                    </p>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processingReserve}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReserve}
              disabled={processingReserve}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {processingReserve ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reserving...
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 mr-2" />
                  Confirm Reservation
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default BookingPage;
